import {
  SPEND_CLASS_ORDER,
  type SpendClass,
  spendClassForCost,
  spendClassForEvents,
} from "@/lib/tina/spend-class";
import type {
  CostDailyRow,
  EventsDailyRow,
  FixedCostDailyRow,
} from "@/types/tina";

/** The two fixed-cost series the chart can add on top of the spend classes. */
export type FixedCostCategory = FixedCostDailyRow["category"];

export const FIXED_COST_CATEGORIES: readonly FixedCostCategory[] = [
  "cloud_run",
  "subscription",
];

export const FIXED_COST_LABEL: Record<FixedCostCategory, string> = {
  cloud_run: "Cloud Run",
  subscription: "Subscriptions",
};

/** One per-day point for the spend chart, after filtering + aggregation. */
export interface CostTrendPoint {
  day: string;
  llm: number;
  api: number;
  total: number;
  /**
   * Scraped events ONLY — never enrichment jobs. An enrichment job is
   * not an event, and folding the two would make the chart's `cost per event`
   * badge quietly wrong the moment enrichment is toggled on.
   */
  events: number;
  /** Terminal enrichment job rows that day — a different unit, its own series. */
  enrichmentJobs: number;
  /** Cloud Run spend that day — 0 while its pill is off. */
  cloudRun: number;
  /** Subscription spend that day — 0 while its pill is off. */
  subscriptions: number;
}

/**
 * Spend classes present across the fetched cost + event rows, in
 * `SPEND_CLASS_ORDER` (drives which toggles the chart offers — theater etc.
 * appear automatically if data exists).
 *
 * This replaced `contentTypesPresent`, which keyed on the raw `content_type`.
 * With `job_class=all` that produced eleven pills — nine of them labelled
 * "Artist enrichment", "Venue enrichment" … — for what is one spend class.
 */
export function spendClassesPresent(
  rows: CostDailyRow[],
  events: EventsDailyRow[],
): SpendClass[] {
  const seen = new Set<SpendClass>();
  for (const r of rows) seen.add(spendClassForCost(r));
  for (const e of events) seen.add(spendClassForEvents(e));
  return SPEND_CLASS_ORDER.filter((cls) => seen.has(cls));
}

/**
 * Aggregate per-(day, stage, content_type) cost rows and per-(day, content_type)
 * event counts into per-day points, including ONLY the enabled spend classes.
 * One enabled class shows that class alone; several enabled sums them (the
 * combined view). Returns points sorted ascending by day.
 *
 * `fixed` adds Cloud Run and subscription spend, one series per category, for
 * the categories in `fixedEnabled` only. An enabled fixed series is part of
 * `total`, so every number derived from `total` includes it. Fixed cost is not
 * split by spend class: it is the platform's cost, not one vertical's. A day
 * with only fixed cost still makes a point, with `events` 0.
 */
export function aggregateCostTrend(
  rows: CostDailyRow[],
  events: EventsDailyRow[],
  enabled: ReadonlySet<SpendClass>,
  fixed: FixedCostDailyRow[] = [],
  fixedEnabled: ReadonlySet<FixedCostCategory> = new Set(),
): CostTrendPoint[] {
  const map = new Map<string, CostTrendPoint>();
  const at = (day: string): CostTrendPoint => {
    let cur = map.get(day);
    if (!cur) {
      cur = {
        day,
        llm: 0,
        api: 0,
        total: 0,
        events: 0,
        enrichmentJobs: 0,
        cloudRun: 0,
        subscriptions: 0,
      };
      map.set(day, cur);
    }
    return cur;
  };

  for (const r of rows) {
    if (!enabled.has(spendClassForCost(r))) continue;
    const total = parseFloat(r.total_cost ?? "0");
    if (!Number.isFinite(total)) continue;
    const llm = parseFloat(r.llm_cost ?? "0");
    const api = parseFloat(r.api_cost ?? "0");
    const cur = at(r.day);
    cur.total += total;
    cur.llm += Number.isFinite(llm) ? llm : 0;
    cur.api += Number.isFinite(api) ? api : 0;
  }
  for (const e of events) {
    const cls = spendClassForEvents(e);
    if (!enabled.has(cls)) continue;
    const point = at(e.day);
    // Enrichment counts are JOBS, and never contribute to `events`.
    if (cls === "enrichment") point.enrichmentJobs += e.events_processed ?? 0;
    else point.events += e.events_processed ?? 0;
  }
  for (const f of fixed) {
    if (!fixedEnabled.has(f.category)) continue;
    const usd = parseFloat(f.cost_usd ?? "0");
    if (!Number.isFinite(usd)) continue;
    const point = at(f.day);
    if (f.category === "cloud_run") point.cloudRun += usd;
    else point.subscriptions += usd;
    point.total += usd;
  }

  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * Window total per spend class, over every fetched cost row regardless of which
 * classes are toggled on.
 *
 * This is what keeps a toggled-OFF class from being *invisible* rather than
 * merely hidden: the chart renders each pill with its own window total, so a
 * curator looking at a music-only chart can still see what enrichment cost.
 */
export function spendByClass(rows: CostDailyRow[]): Record<SpendClass, number> {
  const out = {
    music: 0,
    cinema: 0,
    theater: 0,
    enrichment: 0,
    refresh: 0,
    other: 0,
  } satisfies Record<SpendClass, number>;
  for (const r of rows) {
    const total = parseFloat(r.total_cost ?? "0");
    if (!Number.isFinite(total)) continue;
    out[spendClassForCost(r)] += total;
  }
  return out;
}

/**
 * Window total per fixed-cost category, over every fetched row regardless of
 * which series are toggled on — the fixed-cost twin of `spendByClass`, for the
 * Cloud Run and Subscriptions pills.
 */
export function fixedCostTotals(
  fixed: FixedCostDailyRow[],
): Record<FixedCostCategory, number> {
  const out = { cloud_run: 0, subscription: 0 } satisfies Record<
    FixedCostCategory,
    number
  >;
  for (const f of fixed) {
    const usd = parseFloat(f.cost_usd ?? "0");
    if (!Number.isFinite(usd)) continue;
    out[f.category] += usd;
  }
  return out;
}

/**
 * Terminal enrichment job rows across the whole window — the number the KPI
 * strip's "Events" hint needs so that a scrape-only headline says so out loud
 * instead of quietly meaning something narrower than the chart beside it.
 */
export function enrichmentJobTotal(events: EventsDailyRow[]): number {
  let total = 0;
  for (const e of events) {
    if (spendClassForEvents(e) === "enrichment")
      total += e.events_processed ?? 0;
  }
  return total;
}
